import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../Data/service/global.service';
import {
  AiInvoiceConfirmResponseDto,
  AiInvoiceUiBootstrapDto,
  AiSessionChatResponseDto,
  AiSessionsListResponseDto,
  AiVoiceResponseDto,
  PostAiResolveEntityDto,
  PostAiTextDto,
  StartAiVoiceSessionDto,
  StartAiVoiceSessionResponseDto,
} from '../models/ai-invoice-voice.model';

export type SessionsQuery = {
  limit?: number;
  offset?: number;
  status?: 'active' | 'completed';
  scope?: 'merchant' | 'mine';
};

/**
 * Client for the AI Invoice Chat backend contract at `/ai-invoice` (see `FRONTEND.md`).
 *
 * Endpoints:
 * - `GET    /ai-invoice/ui/bootstrap`
 * - `GET    /ai-invoice/sessions`
 * - `GET    /ai-invoice/sessions/:sessionId/chat`
 * - `POST   /ai-invoice/session/start`
 * - `POST   /ai-invoice/session/:sessionId/text`
 * - `POST   /ai-invoice/session/:sessionId/image`
 * - `POST   /ai-invoice/session/:sessionId/resolve-entity`
 * - `POST   /ai-invoice/session/:sessionId/confirm`
 */
@Injectable({
  providedIn: 'root',
})
export class AiInvoiceVoiceService {
  private readonly prefix = 'ai-invoice';

  constructor(private http: HttpClient) {}

  getBootstrap(): Observable<AiInvoiceUiBootstrapDto> {
    return this.http.get<AiInvoiceUiBootstrapDto>(
      `${baseUrl}${this.prefix}/ui/bootstrap`,
    );
  }

  getSessions(query?: SessionsQuery): Observable<AiSessionsListResponseDto> {
    let params = new HttpParams();
    if (query?.limit != null) params = params.set('limit', String(query.limit));
    if (query?.offset != null) params = params.set('offset', String(query.offset));
    if (query?.status) params = params.set('status', query.status);
    if (query?.scope) params = params.set('scope', query.scope);
    return this.http.get<AiSessionsListResponseDto>(
      `${baseUrl}${this.prefix}/sessions`,
      { params },
    );
  }

  getSessionChat(sessionId: string): Observable<AiSessionChatResponseDto> {
    return this.http.get<AiSessionChatResponseDto>(
      `${baseUrl}${this.prefix}/sessions/${encodeURIComponent(sessionId)}/chat`,
    );
  }

  startSession(body: StartAiVoiceSessionDto): Observable<StartAiVoiceSessionResponseDto> {
    return this.http.post<StartAiVoiceSessionResponseDto>(
      `${baseUrl}${this.prefix}/session/start`,
      body,
    );
  }

  /**
   * Send free text and/or a list selection: `selected_option_id` (single) or `selected_option_ids` (multi).
   * Backend requires `text` unless a selection id is provided.
   */
  postText(sessionId: string, payload: PostAiTextDto): Observable<AiVoiceResponseDto> {
    const body: PostAiTextDto = {};
    const t = payload.text?.trim();
    if (t) body.text = t;
    if (payload.selected_option_ids?.length) {
      body.selected_option_ids = [...payload.selected_option_ids];
    } else if (payload.selected_option_id != null) {
      body.selected_option_id = payload.selected_option_id;
    }
    return this.http.post<AiVoiceResponseDto>(
      `${baseUrl}${this.prefix}/session/${encodeURIComponent(sessionId)}/text`,
      body,
    );
  }

  /**
   * Upload an invoice image (JPEG / PNG / WEBP / GIF / PDF ≤ 10 MB).
   * The backend extracts data and returns the same `AiVoiceResponseDto` as `/text`.
   * Do NOT set `Content-Type` manually — the browser adds the multipart boundary automatically.
   */
  postImage(sessionId: string, file: File, text?: string): Observable<AiVoiceResponseDto> {
    const formData = new FormData();
    formData.append('image', file);
    const t = text?.trim();
    if (t) formData.append('text', t);
    return this.http.post<AiVoiceResponseDto>(
      `${baseUrl}${this.prefix}/session/${encodeURIComponent(sessionId)}/image`,
      formData,
    );
  }

  /**
   * Link a newly-created customer or product to the session and resume the invoice flow.
   * Must be called immediately after the user saves from the add-customer / add-product modal.
   * The response is the same `AiVoiceResponseDto` as `/text`.
   */
  resolveEntity(sessionId: string, body: PostAiResolveEntityDto): Observable<AiVoiceResponseDto> {
    return this.http.post<AiVoiceResponseDto>(
      `${baseUrl}${this.prefix}/session/${encodeURIComponent(sessionId)}/resolve-entity`,
      body,
    );
  }

  confirm(sessionId: string, body: { confirm?: boolean }): Observable<AiInvoiceConfirmResponseDto> {
    return this.http.post<AiInvoiceConfirmResponseDto>(
      `${baseUrl}${this.prefix}/session/${encodeURIComponent(sessionId)}/confirm`,
      body,
    );
  }
}
